import { Ban, Baby, WifiOff, CalendarPlus, SlidersHorizontal, Volume2, BookOpen, EyeOff, Flag } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const CARD =
  "rounded-[20px] border-2 border-[#1A1D1E] p-8 shadow-[0_8px_0_0_#1A1D1E] transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-[0_12px_0_0_#1A1D1E]";

const IconChip = ({ icon: Icon, bg }) => (
  <span className={`mb-5 grid h-12 w-12 place-items-center rounded-[12px] border-2 border-[#1A1D1E] ${bg}`}>
    <Icon className="h-6 w-6" />
  </span>
);

export const Features = () => (
  <section id="how-it-works" data-testid="features-section" className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
    <Reveal>
      <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-[#1FBCA2]">
        What makes TriviaPro different
      </p>
      <h2 className="mt-4 max-w-2xl font-display text-4xl font-bold tracking-tight md:text-5xl">
        Not another trivia app.
        <span className="text-[#6B7280]"> Here's the proof.</span>
      </h2>
    </Reveal>

    <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-6 md:gap-8">
      <Reveal className="md:col-span-4" delay={0.05}>
        <div data-testid="feature-no-ads" className={`${CARD} h-full bg-[#1FBCA2]`}>
          <IconChip icon={Ban} bg="bg-white" />
          <h3 className="font-display text-2xl font-semibold md:text-3xl">No ads. Ever.</h3>
          <p className="mt-3 max-w-md leading-relaxed text-[#1A1D1E]/75">
            Not in the paid version. Not in the free version. Not "just this
            once". TriviaPro carries zero advertising – the game is the
            product, never your family's attention.
          </p>
        </div>
      </Reveal>

      <Reveal className="md:col-span-2" delay={0.12}>
        <div className={`${CARD} h-full overflow-hidden !p-0`}>
          <img
            src="https://images.unsplash.com/photo-1607453998774-d533f65dac99?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
            alt="Three kids laughing and hugging together while playing"
            loading="lazy"
            className="h-full w-full object-cover"
          />
        </div>
      </Reveal>

      <Reveal className="md:col-span-2" delay={0.05}>
        <div data-testid="feature-age-matched" className={`${CARD} h-full bg-[#FFC837]`}>
          <IconChip icon={SlidersHorizontal} bg="bg-white" />
          <h3 className="font-display text-xl font-semibold md:text-2xl">Made for your age</h3>
          <p className="mt-3 text-sm leading-relaxed text-[#1A1D1E]/75">
            Questions adapt to each player's age and ability, so a
            seven-year-old and a grandparent can win the same round.
          </p>
        </div>
      </Reveal>

      <Reveal className="md:col-span-2" delay={0.12}>
        <div data-testid="feature-sensory" className={`${CARD} h-full bg-white`}>
          <IconChip icon={Volume2} bg="bg-[#FF7B72]" />
          <h3 className="font-display text-xl font-semibold md:text-2xl">See it. Hear it.</h3>
          <p className="mt-3 text-sm leading-relaxed text-[#6B7280]">
            Visual and sound questions – picture rounds, audio clips, and
            more – keep every game surprising.
          </p>
        </div>
      </Reveal>

      <Reveal className="md:col-span-2" delay={0.19}>
        <div data-testid="feature-education" className={`${CARD} h-full bg-[#FF7B72]`}>
          <IconChip icon={BookOpen} bg="bg-white" />
          <h3 className="font-display text-xl font-semibold md:text-2xl">Smarter every round</h3>
          <p className="mt-3 text-sm leading-relaxed text-[#1A1D1E]/75">
            Built for learning: every question comes with a bite-sized "did
            you know" fact. Win or lose, you leave knowing something new.
          </p>
        </div>
      </Reveal>

      <Reveal className="md:col-span-2" delay={0.05}>
        <div data-testid="feature-no-data" className={`${CARD} h-full bg-white`}>
          <IconChip icon={EyeOff} bg="bg-[#1FBCA2]" />
          <h3 className="font-display text-xl font-semibold md:text-2xl">Zero personal data</h3>
          <p className="mt-3 text-sm leading-relaxed text-[#6B7280]">
            No accounts, no tracking, no personal data collected. Play the
            full game without telling us who you are.
          </p>
        </div>
      </Reveal>

      <Reveal className="md:col-span-2" delay={0.12}>
        <div data-testid="feature-flag" className={`${CARD} h-full bg-white`}>
          <IconChip icon={Flag} bg="bg-[#FFC837]" />
          <h3 className="font-display text-xl font-semibold md:text-2xl">Flag any question</h3>
          <p className="mt-3 text-sm leading-relaxed text-[#6B7280]">
            Quality obsessed. Spot a wrong or outdated answer? Flag it in one
            tap – every report is reviewed by a human.
          </p>
        </div>
      </Reveal>

      <Reveal className="md:col-span-2" delay={0.19}>
        <div data-testid="feature-kid-safe" className={`${CARD} h-full bg-[#FFC837]`}>
          <IconChip icon={Baby} bg="bg-white" />
          <h3 className="font-display text-xl font-semibold md:text-2xl">Kid-safe questions</h3>
          <p className="mt-3 text-sm leading-relaxed text-[#1A1D1E]/75">
            Every question is reviewed for ages 6+. No sketchy links, no
            surprises.
          </p>
        </div>
      </Reveal>

      <Reveal className="md:col-span-3" delay={0.05}>
        <div data-testid="feature-offline" className={`${CARD} h-full bg-white`}>
          <IconChip icon={WifiOff} bg="bg-[#1FBCA2]" />
          <h3 className="font-display text-xl font-semibold md:text-2xl">Works offline</h3>
          <p className="mt-3 text-sm leading-relaxed text-[#6B7280]">
            Road trips, flights, grandma's Wi-Fi. Question packs download once
            and play anywhere.
          </p>
        </div>
      </Reveal>

      <Reveal className="md:col-span-3" delay={0.12}>
        <div data-testid="feature-weekly" className={`${CARD} h-full bg-[#FF7B72]`}>
          <IconChip icon={CalendarPlus} bg="bg-white" />
          <h3 className="font-display text-xl font-semibold md:text-2xl">Fresh drops weekly</h3>
          <p className="mt-3 text-sm leading-relaxed text-[#1A1D1E]/75">
            New question packs land every week – animals, space, history,
            movies, and gloriously silly stuff.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);
